import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  Group as GroupIcon,
  EmojiEvents as TrophyIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';
import hackathonService from '../../services/hackathonService';

// Helper to format date for datetime-local input
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const HackathonEvents = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: '',
    description: '',
    start_date: formatDateForInput(new Date()),
    end_date: formatDateForInput(new Date(Date.now() + 24 * 60 * 60 * 1000))
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await hackathonService.getEvents();
      setEvents(response.events || []);
    } catch (err) {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    try {
      setCreating(true);
      const response = await hackathonService.createEvent({
        ...newEvent,
        start_date: new Date(newEvent.start_date).toISOString(),
        end_date: new Date(newEvent.end_date).toISOString()
      });
      setCreateDialog(false);
      setNewEvent({
        name: '',
        description: '',
        start_date: formatDateForInput(new Date()),
        end_date: formatDateForInput(new Date(Date.now() + 24 * 60 * 60 * 1000))
      });
      navigate(`/hackathon/${response.event.id}`);
    } catch (err) {
      setError('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'default',
      'registration_open': 'info',
      'registration_closed': 'warning',
      'team_formation': 'secondary',
      'in_progress': 'primary',
      'judging': 'warning',
      'completed': 'success'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Hackathon Events</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage hackathon events, registrations, and scoring
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
        >
          Create Event
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Events Grid */}
      {events.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <TrophyIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No Hackathon Events Yet</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Create your first hackathon event to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Create Event
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {events.map((event) => (
            <Grid item xs={12} sm={6} md={4} key={event.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 6,
                    cursor: 'pointer'
                  }
                }}
                onClick={() => navigate(`/hackathon/${event.id}`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
                      {event.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={event.status?.replace('_', ' ')}
                      color={getStatusColor(event.status)}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {event.description || 'No description'}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EventIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1} mt={2}>
                    <Chip
                      size="small"
                      icon={<GroupIcon />}
                      label={`${event.modules?.length || 0} modules`}
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    endIcon={<ArrowIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/hackathon/${event.id}`);
                    }}
                  >
                    Manage
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Event Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Hackathon Event</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Event Name"
            value={newEvent.name}
            onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={newEvent.description}
            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            label="Start Date & Time"
            type="datetime-local"
            value={newEvent.start_date}
            onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="End Date & Time"
            type="datetime-local"
            value={newEvent.end_date}
            onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateEvent}
            disabled={!newEvent.name || creating}
          >
            {creating ? <CircularProgress size={24} /> : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HackathonEvents;
